import React, { useEffect, useMemo, useState } from 'react';

// In production, default to Netlify Functions path; in local dev, use the Express proxy (/api).
const API_BASE = (
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? '/api'
    : '/.netlify/functions')
).replace(/\/+$/, '');

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function downloadBase64Pdf(base64, filename) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'ATS_Resume.pdf';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function CanHelp() {
  const [jobDescription, setJobDescription] = useState('');
  const [modelChoice, setModelChoice] = useState('best');
  const [loadingReport, setLoadingReport] = useState(false);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);
  const [resumeResult, setResumeResult] = useState(null);
  const [barWidth, setBarWidth] = useState(0);
  const [resumeStatus, setResumeStatus] = useState('idle'); // idle | loading | ready | error
  const [resumeErr, setResumeErr] = useState(null);
  const [coverStatus, setCoverStatus] = useState('idle'); // idle | loading | ready | error
  const [coverData, setCoverData] = useState(null);

  useEffect(() => {
    if (!report) return;
    setBarWidth(0);
    const t = setTimeout(() => setBarWidth(clamp(report.confidence, 0, 100)), 60);
    return () => clearTimeout(t);
  }, [report]);

  const canSubmit = useMemo(
    () => jobDescription.trim().length >= 50 && !loadingReport,
    [jobDescription, loadingReport]
  );

  const charCount = jobDescription.length;

  async function onSubmit() {
    setErr(null);
    setResumeErr(null);
    setReport(null);
    setResumeResult(null);
    setLoadingReport(true);
    setResumeStatus('idle');
    setCoverStatus('idle');
    setCoverData(null);

    try {
      const res = await fetch(`${API_BASE}/can-i-help-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobDescription, modelChoice })
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        console.error('[can-i-help] non-JSON response', res.status, await res.text());
        throw new Error('Service unavailable. Please try again shortly.');
      }
      const data = await res.json();
      if (!res.ok) {
        console.error('[can-i-help] API error', res.status, data);
        throw new Error(data?.error || 'Service unavailable. Please try again shortly.');
      }
      setReport(data);
      setLoadingReport(false);
      // kick off resume generation in the background
      generateResumeStep(data);
    } catch (e) {
      setErr(e?.message || 'Something went wrong.');
    } finally {
      setLoadingReport(false);
    }
  }

  async function generateResumeStep(currentReport) {
    const reportPayload = currentReport || report;
    if (!reportPayload) return;
    setResumeErr(null);
    setResumeStatus('loading');
    setCoverStatus('idle');
    setCoverData(null);
    const plans = [
      { level: 0, label: 'base', note: 'Initial attempt with full detail.' },
      {
        level: 1,
        label: 'trim-tokens',
        note: 'Retry 1: tighter token budget, fewer JD keywords, and trimmed JD to keep things snappy.'
      },
      {
        level: 2,
        label: 'one-page',
        note: 'Retry 2: force a single page and even smaller budget to avoid timeouts.'
      },
      {
        level: 3,
        label: 'fast-lean',
        note: 'Retry 3: use the fast model with the leanest budget and shortest JD slice.'
      }
    ];

    const isTimeoutError = (error) => {
      const message = (error?.message || '').toLowerCase();
      return (
        message.includes('timed out') ||
        message.includes('timeout') ||
        error?.code === 'ETIMEDOUT' ||
        error?.name === 'AbortError' ||
        error?.status === 504
      );
    };

    const runAttempt = async (plan) => {
      const res = await fetch(`${API_BASE}/generate-resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobDescription,
          report: reportPayload,
          modelChoice,
          retryLevel: plan.level
        })
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        const err = new Error('Resume generation unavailable right now.');
        err.status = res.status;
        err.details = text;
        throw err;
      }
      const data = await res.json();
      if (!res.ok) {
        const err = new Error(data?.error || 'Resume generation unavailable right now.');
        err.status = res.status;
        err.code = data?.code;
        throw err;
      }
      return data;
    };

    let lastError = null;
    for (let i = 0; i < plans.length; i += 1) {
      const plan = plans[i];
      try {
        if (plan.level > 0) {
          console.info(`[generate-resume] retry ${plan.level} starting: ${plan.note}`);
        }
        const data = await runAttempt(plan);
        setResumeResult(data);
        setResumeStatus('ready');
        // chain cover letter after resume is ready
        generateCoverLetter(data, reportPayload);
        return;
      } catch (e) {
        lastError = e;
        const timeoutLike = isTimeoutError(e);
        const nextPlan = plans[i + 1];
        console.error(`[generate-resume] attempt ${i + 1} (${plan.label}) failed`, e);
        if (timeoutLike && nextPlan) {
          console.info(`[generate-resume] ${plan.note} -> retrying with: ${nextPlan.note}`);
          continue;
        }
        break;
      }
    }

    setResumeErr(lastError?.message || 'Resume generation unavailable right now.');
    setResumeStatus('error');
  }

  async function generateCoverLetter(baseResume, baseReport) {
    const resumePayload = baseResume || resumeResult;
    if (!resumePayload || !resumePayload.resume) return;
    setCoverStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/generate-cover-letter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobDescription,
          resume: resumePayload.resume,
          report: baseReport || report,
          modelChoice
        })
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        console.error('[cover-letter] non-JSON response', res.status, await res.text());
        throw new Error('Cover letter unavailable right now.');
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Cover letter unavailable right now.');
      }
      setCoverData(data);
      setCoverStatus('ready');
    } catch (e) {
      console.error('[cover-letter] error', e);
      setCoverStatus('error');
    }
  }

  return (
    <main className="relative flex-1 bg-white text-neutral-800">
      <div className="relative container mx-auto w-full max-w-5xl px-4 sm:px-6 pb-10 text-left">
        <header className="sticky top-16 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 mb-6 bg-white/95 backdrop-blur border-b border-zinc-200">
          <div className="max-w-5xl">
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-neutral-900">
              Dalmo Can Help
            </h1>
            <p className="mt-2 text-zinc-600 max-w-2xl">
              Type or paste any job description and check whether or not Dalmo can help.
            </p>
          </div>
        </header>

        <section className="rounded-2xl border border-zinc-900/10 bg-white shadow-md">
          <div className="p-5 sm:p-7 space-y-6">
            {!report ? (
              <>
                <label className="text-sm font-medium text-zinc-800">Job description</label>
                <textarea
                  className="mt-2 w-full min-h-[220px] rounded-xl border border-zinc-900/10 bg-white px-4 py-3 text-sm text-zinc-900 outline-none shadow-sm focus:ring-2 focus:ring-emerald-400/40 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500"
                  placeholder="Paste the full job description here"
                  value={jobDescription}
                  disabled={loadingReport}
                  onChange={(e) => setJobDescription(e.target.value)}
                />
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 text-sm text-zinc-700">
                    <span className="font-medium text-zinc-800">Model:</span>
                    <div className="inline-flex rounded-full border border-zinc-300 bg-white p-1">
                      {[
                        { value: 'best', label: 'Best', hint: 'gpt-5.2' },
                        { value: 'fast', label: 'Fast', hint: 'gpt-5-nano' }
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={loadingReport}
                          onClick={() => setModelChoice(opt.value)}
                          className={
                            'px-3 py-1 text-sm rounded-full transition ' +
                            (modelChoice === opt.value
                              ? 'bg-zinc-900 text-white shadow-sm'
                              : 'text-zinc-700 hover:bg-zinc-100')
                          }
                          title={opt.hint}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={onSubmit}
                    disabled={!canSubmit}
                    className="inline-flex items-center justify-center rounded-xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {loadingReport ? 'Thinking...' : 'Can Dalmo Help?'}
                  </button>

                  <div className="sm:ml-auto text-xs text-zinc-500">
                    {charCount.toLocaleString()} characters (min 50)
                  </div>
                </div>

                {err ? (
                  <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-sm text-rose-700">
                    {err}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="space-y-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div
                      className={
                        report.verdict === 'Yes'
                          ? 'text-4xl sm:text-5xl font-semibold tracking-tight text-teal-600'
                          : 'text-4xl sm:text-5xl font-semibold tracking-tight text-rose-500'
                      }
                    >
                      {report.verdict === 'Yes' ? 'Yes!' : 'Probably Not'}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setReport(null);
                      setJobDescription('');
                      setResumeResult(null);
                      setResumeErr(null);
                      setErr(null);
                      setLoadingReport(false);
                      setCoverStatus('idle');
                      setCoverData(null);
                      setResumeStatus('idle');
                    }}
                    className="inline-flex items-center justify-center rounded-xl border border-zinc-900/10 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 shadow-sm hover:bg-zinc-50"
                  >
                    Analyze another job
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-medium text-zinc-800">
                      Confidence Level: {Math.min(99, Math.max(0, report.confidence))}%
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-zinc-900/10 overflow-hidden">
                    <div
                      className={
                        'h-full transition-all duration-700 ease-out ' +
                        (report.verdict === 'Yes' ? 'bg-teal-500' : 'bg-rose-500')
                      }
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-zinc-500">Here&apos;s why:</div>
                  <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-zinc-800">
                    {report.pitch_bullets.slice(0, 5).map((b, idx) => (
                      <li key={idx}>{b}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-zinc-500">Growth &amp; reach</div>
                  {report.growth_reach.length ? (
                    <ul className="mt-3 space-y-3">
                      {report.growth_reach.slice(0, 3).map((g, idx) => (
                        <li key={idx} className="rounded-xl border border-zinc-900/10 bg-white p-4 shadow-sm">
                          <div className="text-sm font-semibold text-zinc-900">{g.jd_point}</div>
                          <div className="mt-1 text-sm text-zinc-700">{g.framing}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-3 text-sm text-zinc-600">No obvious gaps surfaced from the resume text.</div>
                  )}
                </div>

                {report.blockers?.length ? (
                  <div className="rounded-xl border border-zinc-900/10 bg-white p-4 shadow-sm">
                    <div className="text-xs uppercase tracking-wide text-zinc-500">Not all requirements are met.</div>
                    <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-zinc-800">
                      {report.blockers.slice(0, 3).map((x, idx) => (
                        <li key={idx}>{x}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      if (resumeStatus === 'ready' && resumeResult?.pdf_base64) {
                        downloadBase64Pdf(resumeResult.pdf_base64, resumeResult.pdf_filename);
                      } else if (resumeStatus !== 'loading') {
                        generateResumeStep(report);
                      }
                    }}
                    disabled={resumeStatus === 'loading'}
                    className={
                      'inline-flex items-center justify-center rounded-xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition ' +
                      (resumeStatus === 'loading' ? 'opacity-60 cursor-wait' : 'hover:bg-zinc-800')
                    }
                  >
                    {resumeStatus === 'ready'
                      ? 'Download Custom Resume'
                      : resumeStatus === 'loading'
                      ? 'Generating Resume...'
                      : resumeStatus === 'error'
                      ? 'Retry Resume'
                      : 'Generate Resume'}
                  </button>
                  {resumeErr ? (
                    <div className="text-sm text-rose-600 sm:self-center">{resumeErr}</div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      if (coverStatus === 'ready' && coverData?.cover_letter_pdf_base64) {
                        downloadBase64Pdf(
                          coverData.cover_letter_pdf_base64,
                          coverData.cover_letter_pdf_filename || 'Cover_Letter.pdf'
                        );
                      } else if (coverStatus !== 'loading') {
                        generateCoverLetter(resumeResult, report);
                      }
                    }}
                    disabled={coverStatus === 'loading' || resumeStatus !== 'ready'}
                    className={
                      'inline-flex items-center justify-center rounded-xl border border-zinc-900/10 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 shadow-sm ' +
                      (coverStatus === 'loading'
                        ? 'opacity-60 cursor-wait'
                        : resumeStatus !== 'ready'
                        ? 'opacity-60 cursor-not-allowed'
                        : 'hover:bg-zinc-50')
                    }
                  >
                    {coverStatus === 'ready'
                      ? 'Download Cover Letter'
                      : coverStatus === 'loading'
                      ? 'Generating Cover Letter...'
                      : coverStatus === 'error'
                      ? 'Retry Cover Letter'
                      : 'Generate Cover Letter'}
                  </button>

                  <a
                    href="https://calendly.com/dalmomendonca/consulting"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-xl border border-zinc-900/10 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 shadow-sm hover:bg-zinc-50"
                  >
                    Schedule a Call
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

export default CanHelp;
